// Dependencies
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
// Core & Lab
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
// Icons
import Close from '@mui/icons-material/Close';
import { AppState } from '../../reducers';
import { Button, DialogActions, DialogContent, FormControl, FormHelperText, Grid, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as locales from 'date-fns/locale';
import i18next from 'i18next';
import { DATE_ERROR, validateDate } from '../utils/input/formControl';
import { useSnackbar } from 'notistack';
import { isAfter, startOfDay } from 'date-fns';

export const ChannelManagerStockModal: FC<{ open: boolean; onClose: () => void; onSaveForm: (date_criteria: { start_date: Date | null, end_date: Date | null }) => void }> = ({ open, onClose, onSaveForm }): ReactElement => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const locales_cpy: { [key: string]: any } = locales;
    const contract_selected = useSelector((state: AppState) => state.channelManager.contract_selected);
    const edit_step = useSelector((state: AppState) => state.channelManager.edit_step);
    const [date_criteria_form, setDateCriteriaForm] = useState<{ start_date: Date | null; start_date_error?: string; end_date: Date | null; end_date_error?: string }>({ start_date: null, end_date: null });

    const date_criteria_form_end_date = useMemo(() => {
        if (!contract_selected?.end_date || !contract_selected?.start_date) return null;

        const contractEndDate = new Date(contract_selected.end_date);
        const maxAllowedEndDate = new Date(contract_selected.start_date);
        maxAllowedEndDate.setFullYear(maxAllowedEndDate.getFullYear() + 1); // Add 365 days

        return maxAllowedEndDate > contractEndDate ? contractEndDate : maxAllowedEndDate;
    }, [contract_selected]);

    const max_end_date = useMemo(() => {
        if (!contract_selected?.end_date) return undefined;
        if (!date_criteria_form.start_date) return contract_selected?.end_date;

        const contractEndDate = new Date(contract_selected?.end_date);
        const maxAllowedEndDate = new Date(date_criteria_form.start_date);
        maxAllowedEndDate.setFullYear(maxAllowedEndDate.getFullYear() + 1); // Add 365 days

        return maxAllowedEndDate > contractEndDate ? contractEndDate : maxAllowedEndDate;
    }, [contract_selected, date_criteria_form.start_date]);



    const onStartDateChange = (date: Date | null) => {
        setDateCriteriaForm((state) => ({ ...state, start_date: date, end_date: null, start_date_error: undefined, end_date_error: undefined }));
    };
    const onEndDateChange = (date: Date | null) => {
        setDateCriteriaForm((state) => ({ ...state, end_date: date, end_date_error: undefined }));

    };

    const onSave = () => {
        let is_error = false;
        const start_date = date_criteria_form.start_date ? startOfDay(date_criteria_form.start_date) : null;
        const end_date = date_criteria_form.end_date ? startOfDay(date_criteria_form.end_date) : null;
        const normalized_max_end_date = max_end_date ? startOfDay(new Date(max_end_date)) : null;
        const start_date_errors = validateDate(start_date);
        const start_date_error = start_date_errors.includes(DATE_ERROR.REQUIRED) ? t('shared.date-required') : (start_date_errors.includes(DATE_ERROR.INVALID) ? t('shared.invalid-date') : (start_date_errors.includes(DATE_ERROR.FORMAT_ERROR) ? t('shared.date-format-error') : ''));
        if (start_date_error) {
            enqueueSnackbar(start_date_error, { variant: 'error' });
            setDateCriteriaForm((state) => ({ ...state, start_date_error }));
            is_error = true;
        }
        const end_date_errors = validateDate(end_date, start_date);
        const end_date_error = end_date_errors.includes(DATE_ERROR.REQUIRED) ? t('shared.date-required') : (end_date_errors.includes(DATE_ERROR.INVALID) ? t('shared.invalid-date') : (end_date_errors.includes(DATE_ERROR.FORMAT_ERROR) ? t('shared.date-format-error') : (end_date_errors.includes(DATE_ERROR.INF_TO_MIN_DATE) ? t('shared.end-date-should-sup') : '')));
        if (end_date_error) {
            enqueueSnackbar(end_date_error, { variant: 'error' });
            setDateCriteriaForm((state) => ({ ...state, end_date_error }));
            is_error = true;
        }
        if (normalized_max_end_date && end_date && isAfter(end_date, normalized_max_end_date)) {
            enqueueSnackbar(t('shared.end-date-exed-contract-or-365days'), { variant: 'error' });
            setDateCriteriaForm((state) => ({ ...state, end_date_error: t('shared.end-date-exed-contract-or-365days') }));
            is_error = true;
        }
        if (!!date_criteria_form.start_date_error || !!date_criteria_form.start_date_error || is_error) {
            return;
        }
        onSaveForm(date_criteria_form as { start_date: Date; end_date: Date });
    };

    useEffect(() => {
        setDateCriteriaForm({ start_date: contract_selected?.start_date ? new Date(contract_selected?.start_date) : null, end_date: date_criteria_form_end_date });
    }, [contract_selected]);

    return (
        <>
            <Dialog fullWidth maxWidth={'sm'} open={edit_step === 'STOCKS_STATE' && open} onClose={onClose}>
                <AppBar className={'ft-user-appBar'}>
                    <Toolbar>
                        <IconButton edge={'start'} color={'inherit'} onClick={onClose}><Close /></IconButton>
                        <Typography variant={'h6'}>{t('shared.stock-status-period')}</Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers>
                    <Grid container spacing={2} justifyContent={'center'} alignItems={'center'}>
                        <Grid item xs={6}>
                            <FormControl fullWidth error={!!date_criteria_form.start_date_error}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales_cpy[i18next.language.replace("-", "")]}>
                                    <DatePicker
                                        label={t('shared.start-date')}
                                        value={date_criteria_form.start_date}
                                        maxDate={contract_selected?.end_date || undefined}
                                        minDate={contract_selected?.start_date || undefined}
                                        onChange={onStartDateChange}
                                        InputProps={{
                                            startAdornment: (
                                                <IconButton
                                                    onClick={() => onStartDateChange(null)}
                                                    style={{ order: 1 }}
                                                >
                                                    <Close />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: 'end',
                                            style: { order: 2, marginLeft: 0 }
                                        }}
                                        renderInput={(params) => <TextField {...params} required fullWidth error={!!date_criteria_form.start_date_error} />}
                                    />
                                </LocalizationProvider>
                                <FormHelperText>{date_criteria_form.start_date_error}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth error={!!date_criteria_form.end_date_error}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales_cpy[i18next.language.replace("-", "")]}>
                                    <DatePicker
                                        label={t('shared.end-date')}
                                        value={date_criteria_form.end_date}
                                        onChange={onEndDateChange}
                                        minDate={date_criteria_form.start_date || undefined}
                                        maxDate={max_end_date}
                                        InputProps={{
                                            startAdornment: (
                                                <IconButton
                                                    onClick={() => onEndDateChange(null)}
                                                    style={{ order: 1 }}
                                                >
                                                    <Close />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: 'end',
                                            style: { order: 2, marginLeft: 0 }
                                        }}
                                        defaultCalendarMonth={date_criteria_form.start_date || undefined}
                                        renderInput={(params) => <TextField {...params} required fullWidth error={!!date_criteria_form.end_date_error} />}
                                    />
                                </LocalizationProvider>
                                <FormHelperText>{date_criteria_form.end_date_error}</FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color={'inherit'} onClick={onClose}>{t('shared.cancel')}</Button>
                    <Button onClick={onSave}>{t('shared.save')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
